import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Toast from '../shared/ui/Toast';

export type ToastVariant = 'default' | 'success' | 'error';

type ToastContextValue = {
  /** Show a toast (default variant: success). Use for save/create/update/delete confirmations. */
  showToast: (message: string, variant?: ToastVariant) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  const [variant, setVariant] = useState<ToastVariant>('success');

  const showToast = useCallback((msg: string, v: ToastVariant = 'success') => {
    setVariant(v);
    setMessage(msg.trim() || null);
  }, []);

  const onDismiss = useCallback(() => setMessage(null), []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast message={message} onDismiss={onDismiss} variant={variant} position="top-right" />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}

/** Same as useToast but returns null outside ToastProvider (e.g. shared components on the public site). */
export function useToastOptional(): ToastContextValue | null {
  return useContext(ToastContext);
}
